import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import heroImg from '@/assets/hero-solar.jpg';

// Bolitas mobile — agrupadas lado derecho
const PARTICLES_MOBILE = [
  { size: 8,  top: '22%', left: '70%', delay: 0,    dur: 2.8 },
  { size: 6,  top: '58%', left: '83%', delay: 0.5,  dur: 3.2 },
  { size: 10, top: '35%', left: '87%', delay: 0.9,  dur: 2.5 },
  { size: 7,  top: '72%', left: '62%', delay: 0.3,  dur: 3.6 },
  { size: 5,  top: '45%', left: '76%', delay: 1.4,  dur: 2.9 },
  { size: 9,  top: '15%', left: '55%', delay: 0.7,  dur: 3.1 },
  { size: 6,  top: '80%', left: '78%', delay: 1.1,  dur: 2.7 },
];

// Bolitas desktop — distribuidas por toda la pantalla
const PARTICLES_DESKTOP = [
  // Esquina superior izquierda
  { size: 7,  top: '12%', left: '8%',  delay: 0,    dur: 3.0 },
  { size: 5,  top: '28%', left: '4%',  delay: 0.8,  dur: 2.6 },
  // Lado izquierdo medio
  { size: 9,  top: '55%', left: '6%',  delay: 0.4,  dur: 3.4 },
  { size: 6,  top: '75%', left: '12%', delay: 1.2,  dur: 2.8 },
  // Centro-arriba (por encima del texto)
  { size: 5,  top: '8%',  left: '38%', delay: 0.6,  dur: 3.1 },
  { size: 8,  top: '6%',  left: '62%', delay: 1.0,  dur: 2.7 },
  // Lado derecho arriba
  { size: 10, top: '14%', left: '88%', delay: 0.2,  dur: 2.9 },
  { size: 6,  top: '30%', left: '93%', delay: 1.5,  dur: 3.3 },
  // Lado derecho medio-bajo
  { size: 8,  top: '58%', left: '91%', delay: 0.7,  dur: 2.5 },
  { size: 5,  top: '78%', left: '85%', delay: 0.3,  dur: 3.6 },
  // Centro-abajo
  { size: 7,  top: '88%', left: '45%', delay: 1.1,  dur: 2.8 },
  { size: 6,  top: '82%', left: '28%', delay: 0.9,  dur: 3.2 },
];

export default function HeroSection() {
  const [phase, setPhase] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    timerRef.current.push(setTimeout(() => setPhase(1), 120));
    timerRef.current.push(setTimeout(() => setPhase(2), 380));
    timerRef.current.push(setTimeout(() => setPhase(3), 620));
    return () => timerRef.current.forEach(clearTimeout);
  }, []);

  return (
    <>
      {/* ════════════════════════════════════════
          MOBILE HERO — animated compact banner
      ════════════════════════════════════════ */}
      <section
        className="md:hidden relative w-full overflow-hidden"
        style={{ height: '320px' }}
      >
        {/* Real photo background with subtle zoom-in */}
        <img
          src={heroImg}
          alt="Paneles solares en Cuba al atardecer"
          className="absolute inset-0 w-full h-full object-cover"
          decoding="async"
          style={{
            objectPosition: 'center 40%',
            transform: phase >= 1 ? 'scale(1.04)' : 'scale(1.10)',
            transition: 'transform 6s cubic-bezier(0.25,0.46,0.45,0.94)',
          }}
        />

        {/* Gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(110deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.55) 42%, rgba(0,0,0,0.22) 70%, rgba(0,0,0,0.10) 100%)',
          }}
        />

        {/* Warm amber glow */}
        <div
          className="absolute pointer-events-none"
          style={{
            width: '200px', height: '200px',
            top: '-30px', left: '-40px',
            background: 'radial-gradient(circle, rgba(75,191,223,0.18) 0%, transparent 70%)',
            filter: 'blur(40px)',
            opacity: phase >= 2 ? 1 : 0,
            transition: 'opacity 1.2s ease 0.3s',
          }}
        />

        {/* ── Bolitas flotantes mobile ── */}
        {PARTICLES_MOBILE.map((p, i) => (
          <div
            key={i}
            className="absolute rounded-full pointer-events-none"
            style={{
              width: p.size,
              height: p.size,
              top: p.top,
              left: p.left,
              background: i % 3 === 0
                ? 'rgba(75,191,223,0.90)'
                : i % 3 === 1
                  ? 'rgba(255,255,255,0.70)'
                  : 'rgba(30,45,74,0.80)',
              boxShadow: `0 0 ${p.size * 2}px ${p.size * 0.8}px rgba(75,191,223,0.35)`,
              opacity: phase >= 3 ? 1 : 0,
              transition: `opacity 0.5s ease ${p.delay + 0.6}s`,
              animation: phase >= 3
                ? `bounceDot ${p.dur}s ease-in-out ${p.delay}s infinite alternate`
                : 'none',
            }}
          />
        ))}

        {/* Horizontal light streak */}
        <div
          className="absolute pointer-events-none"
          style={{
            height: '1px',
            left: 0, right: 0,
            top: '60%',
            background: 'linear-gradient(90deg, transparent 0%, rgba(75,191,223,0.30) 30%, rgba(75,191,223,0.12) 60%, transparent 100%)',
            opacity: phase >= 2 ? 1 : 0,
            transition: 'opacity 0.8s ease 0.5s',
          }}
        />

        {/* Content — flex column, justified so buttons stay at bottom */}
        <div
          className="relative z-10 flex flex-col h-full px-5 pb-4"
          style={{ paddingTop: '72px' }}
        >
          {/* Badge */}
          <div
            className="flex items-center gap-1.5 mb-2.5 self-start"
            style={{
              opacity: phase >= 1 ? 1 : 0,
              transform: phase >= 1 ? 'translateX(0)' : 'translateX(-10px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0" style={{ background: '#4bbfdf' }} />
            <span
              className="text-[10px] font-bold uppercase"
              style={{ color: 'rgba(75,191,223,0.90)', letterSpacing: '0.16em' }}
            >
              Tienda solar · La Habana, Cuba
            </span>
          </div>

          {/* Title */}
          <div
            style={{
              opacity: phase >= 2 ? 1 : 0,
              transform: phase >= 2 ? 'translateY(0)' : 'translateY(14px)',
              transition: 'opacity 0.45s ease, transform 0.45s ease',
            }}
          >
            <div className="leading-none mb-1.5">
              <span
                className="block font-extrabold text-white"
                style={{ fontSize: '28px', lineHeight: 1.1, letterSpacing: '-0.02em' }}
              >
                Que falte la luz,
              </span>
              <span
                className="block font-extrabold"
                style={{
                  fontSize: '28px',
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                  background: 'linear-gradient(90deg, #4bbfdf 0%, #2aa8cc 45%, #a5e8f5 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                pero no en tu casa
              </span>
            </div>
            <p
              className="font-medium leading-snug"
              style={{
                fontSize: '11.5px',
                color: 'rgba(255,255,255,0.50)',
                maxWidth: '210px',
                letterSpacing: '0.01em',
              }}
            >
              Tienda de paneles y sistemas solares en La Habana
            </p>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Buttons + live badge in same row */}
          <div
            className="flex items-center gap-2"
            style={{
              opacity: phase >= 3 ? 1 : 0,
              transform: phase >= 3 ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
            }}
          >
            <a
              href="/products"
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full font-bold active:scale-95 transition-transform cursor-pointer whitespace-nowrap"
              style={{ background: '#4bbfdf', color: '#fff', fontSize: '12px' }}
            >
              <i className="ri-store-2-line text-xs" />
              Ver catálogo
            </a>
            <a
              href="/contact"
              className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-full font-semibold active:scale-95 transition-transform cursor-pointer whitespace-nowrap"
              style={{
                background: 'rgba(255,255,255,0.10)',
                border: '1px solid rgba(255,255,255,0.25)',
                color: 'rgba(255,255,255,0.85)',
                fontSize: '12px',
              }}
            >
              <i className="ri-whatsapp-line text-xs" />
              Contactar
            </a>
            {/* Live badge inline — no se superpone */}
            <div
              className="ml-auto flex items-center gap-1"
              style={{
                opacity: phase >= 3 ? 1 : 0,
                transition: 'opacity 0.5s ease 0.3s',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
              <span className="text-[9.5px] font-semibold whitespace-nowrap" style={{ color: 'rgba(255,255,255,0.45)' }}>
                Envío La Habana
              </span>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes bounceDot {
            0%   { transform: translateY(0px) scale(1); }
            50%  { transform: translateY(-10px) scale(1.15); }
            100% { transform: translateY(-6px) scale(1.05); }
          }
        `}</style>
      </section>

      {/* ════════════════════════════════════════
          DESKTOP HERO — full-bleed real photo
      ════════════════════════════════════════ */}
      <section
        className="hidden md:flex relative w-full overflow-hidden items-center justify-center"
        style={{ minHeight: '580px' }}
      >
        <div className="absolute inset-0">
          <img
            src={heroImg}
            alt="Paneles solares en Cuba al atardecer"
            className="w-full h-full object-cover"
            decoding="async"
            style={{ objectPosition: 'center 35%' }}
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.40) 40%, rgba(0,0,0,0.50) 70%, rgba(0,0,0,0.72) 100%)' }} />
        </div>

        {/* ── Bolitas flotantes desktop ── */}
        {PARTICLES_DESKTOP.map((p, i) => (
          <div
            key={i}
            className="absolute rounded-full pointer-events-none z-10"
            style={{
              width: p.size,
              height: p.size,
              top: p.top,
              left: p.left,
              background: i % 3 === 0
                ? 'rgba(75,191,223,0.92)'
                : i % 3 === 1
                  ? 'rgba(255,255,255,0.75)'
                  : 'rgba(30,45,74,0.80)',
              boxShadow: `0 0 ${p.size * 2.5}px ${p.size}px rgba(75,191,223,0.40)`,
              opacity: phase >= 3 ? 1 : 0,
              transition: `opacity 0.6s ease ${p.delay + 0.5}s`,
              animation: phase >= 3
                ? `bounceDot ${p.dur}s ease-in-out ${p.delay}s infinite alternate`
                : 'none',
            }}
          />
        ))}

        <div
          className="relative z-10 w-full flex flex-col items-center justify-center text-center px-5 pt-20 pb-14"
          style={{ minHeight: '580px' }}
        >
          <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/25 text-white/90 text-[11px] font-bold px-3.5 py-1.5 rounded-full mb-6 uppercase tracking-widest backdrop-blur-sm">
            <i className="ri-store-2-line" style={{ color: '#4bbfdf' }} />
            Tienda solar · La Habana, Cuba
          </span>
          <h1
            className="font-extrabold text-white leading-tight mb-4 max-w-3xl"
            style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
            }}
          >
            Que falte la luz,{' '}
            <span
              style={{
                background: 'linear-gradient(90deg, #4bbfdf 0%, #2aa8cc 50%, #a5e8f5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              pero no en tu casa
            </span>
          </h1>
          <p className="text-white/65 text-base md:text-lg mb-9 max-w-xl leading-relaxed font-medium">
            Tienda de paneles solares y sistemas solares completos en La Habana, Cuba: paneles, inversores, baterías y kits de respaldo. Envíos en La Habana y servicio de instalación.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="/products"
              className="inline-flex items-center justify-center gap-2 font-bold px-8 py-3.5 rounded-full text-sm transition-all duration-200 cursor-pointer whitespace-nowrap active:scale-95"
              style={{ background: '#4bbfdf', color: '#fff' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#2aa8cc'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#4bbfdf'; }}
            >
              <i className="ri-store-2-line text-base" />
              Ver catálogo
            </a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center gap-2 border border-white/40 hover:border-white/80 hover:bg-white/10 text-white font-semibold px-8 py-3.5 rounded-full text-sm transition-all duration-200 cursor-pointer whitespace-nowrap backdrop-blur-sm"
            >
              <i className="ri-phone-line text-base" />
              Contactar
            </a>
          </div>
          <div className="flex items-center gap-10 mt-10">
            {[
              { icon: 'ri-sun-line',    label: 'Energía solar' },
              { icon: 'ri-hammer-line', label: 'Herramientas' },
              { icon: 'ri-box-3-line',  label: 'Materia prima' },
              { icon: 'ri-truck-line',  label: 'Envío La Habana' },
            ].map(({ icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-1.5">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
                  <i className={`${icon} text-white text-base`} />
                </div>
                <span className="text-white/60 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes bounceDot {
            0%   { transform: translateY(0px) scale(1); }
            50%  { transform: translateY(-10px) scale(1.15); }
            100% { transform: translateY(-6px) scale(1.05); }
          }
        `}</style>
      </section>
    </>
  );
}